import { Rng } from '../core/rng';
import { clamp, Vec3 } from '../core/math';
import { halfBreadthXY, deckHalfWidth } from '../data/ship';
import { sectionAt } from './damage';
import { hostile } from './captain';
import type { BoardingPhase, SimEvents } from './events';
import type { Vessel } from './vessel';

// Ships touching: collisions (and deliberate ramming) and grappling alongside for a boarding fight.

const STATIONS = [-14, -9, -4, 1, 6, 11, 15.5];

/** How far grapnels carry across the water between two hulls, side to side (m). */
export const GRAPNEL_REACH = 16;
/** Moving apart or past each other faster than this (m/s), the lines would only part. */
export const GRAPNEL_SPEED = 3;
/** Hulls this close (m of water between them) and the boarders can get across. */
export const BOARDING_GAP = 3.5;
/** Grappled ships dragged this far apart (m of water between them) part their lines. */
export const LINE_LENGTH = 25;

/** Water between two hulls lying side by side, m (roughly: from their centres and beams). */
export function hullGap(a: Vessel, b: Vessel): number {
  return Math.hypot(a.ship.com.x - b.ship.com.x, a.ship.com.z - b.ship.com.z) - 5.5 * (a.ship.sz + b.ship.sz);
}

/** A ship that can still be boarded: afloat and not already somebody's prize. */
export function boardable(v: Vessel): boolean {
  return v.state === 'active' || v.state === 'struck';
}

/**
 * Could `a` throw grapnels aboard `b` right now? `gap` is the water between
 * the hulls (m), `rel` their speed relative to each other (m/s), and `why`
 * says what's wrong if not ('far', 'fast', 'prize'). The HUD's boarding
 * prompt and the sim both go by this.
 */
export function canGrapple(a: Vessel, b: Vessel): { ok: boolean; gap: number; rel: number; why: '' | 'far' | 'fast' | 'prize' } {
  const gap = hullGap(a, b);
  const rel = Math.hypot(a.ship.vel.x - b.ship.vel.x, a.ship.vel.z - b.ship.vel.z);
  const why = !boardable(b) || !a.fighting ? 'prize' : gap > GRAPNEL_REACH ? 'far' : rel > GRAPNEL_SPEED ? 'fast' : '';
  return { ok: why === '', gap, rel, why };
}

/** Cast off whatever `v` is grappled to (both ends of the lines). */
export function castOff(v: Vessel, vessels: readonly Vessel[]): void {
  if (v.boardingWith < 0) return;
  const o = vessels.find((x) => x.id === v.boardingWith);
  if (o && o.boardingWith === v.id) (o.boardingWith = -1), (o.boardingSide = 0);
  v.boardingWith = -1;
  v.boardingSide = 0;
}

/**
 * Dev: make two ships fast to each other at once, whatever the rules for
 * throwing grapnels say (range, speed, who may board whom). From then on
 * they're hauled together and fought over like any other boarding.
 */
export function lash(a: Vessel, b: Vessel, vessels: readonly Vessel[], events: SimEvents): void {
  if (a.boardingWith === b.id && b.boardingWith === a.id) return;
  castOff(a, vessels);
  castOff(b, vessels);
  a.boardingWith = b.id;
  b.boardingWith = a.id;
  events.push({ type: 'boarding', a: a.id, b: b.id, x: (a.ship.com.x + b.ship.com.x) / 2, z: (a.ship.com.z + b.ship.com.z) / 2 });
}

interface Sphere {
  x: number;
  y: number;
  z: number;
  r: number;
  xd: number;
}

function hullSpheres(v: Vessel, out: Sphere[]): Sphere[] {
  out.length = 0;
  const s = v.ship;
  for (const xd of STATIONS) {
    const hb = Math.max(0.8, halfBreadthXY(xd, 0.3));
    const p = s.designToWorld(tmp.set(xd, 0.4, 0), tmp);
    out.push({ x: p.x, y: p.y, z: p.z, r: hb * s.sz, xd });
  }
  return out;
}

/**
 * How deep two hulls overlap, m (zero or less when they're clear), by the
 * same spheres collisions are worked out with.
 */
export function hullOverlap(a: Vessel, b: Vessel): number {
  const reach = 22 * (a.ship.sx + b.ship.sx) + 10;
  if (!(Math.abs(a.ship.com.x - b.ship.com.x) <= reach && Math.abs(a.ship.com.z - b.ship.com.z) <= reach)) return -Infinity;
  const sa = hullSpheres(a, []), sb = hullSpheres(b, []);
  let worst = -Infinity;
  for (const p of sa) for (const q of sb) worst = Math.max(worst, p.r + q.r - Math.hypot(p.x - q.x, p.z - q.z));
  return worst;
}

/**
 * A boarding fight: the ship whose people threw the grapnels and are
 * fighting their way aboard, and the ship boarded. `hold` is who holds her
 * deck: the boarders thrown back to their own rail at 0, her deck carried at
 * 1. It swings each round with the weight of men in the fight, their heart
 * and their practice, and a share of luck - so a close fight sways back and
 * forth - and those losing ground lose the more men.
 */
export interface BoardingFight {
  attacker: number;
  defender: number;
  hold: number;
  phase: BoardingPhase;
  /** s since the boarders got across. */
  time: number;
  /** The weight of each side in the last round, and the men each has lost. */
  weightA: number;
  weightD: number;
  lostA: number;
  lostD: number;
  /** -1..1: the way the last few rounds went, for the attacker. */
  swing: number;
}

/** Where the boarders start: across her rail with a foothold on her deck. */
const START_HOLD = 0.3;
const PHASES: [BoardingPhase, number][] = [['rail', 0.2], ['foothold', 0.5], ['driving', 0.78], ['breaking', 1.01]];

/**
 * Where `hold` stands, in words: driven back to the rail, a foothold, driving
 * them aft, their defence breaking. Changed from `was` only once the deck has
 * clearly changed hands (so it isn't said back and forth every few seconds).
 */
function phaseOf(hold: number, was?: BoardingPhase): BoardingPhase {
  const raw = PHASES.find(([, top]) => hold < top)![0];
  if (!was || raw === was || !PHASES.some(([p]) => p === was)) return raw;
  const i = PHASES.findIndex(([p]) => p === was);
  const lo = i > 0 ? PHASES[i - 1]![1] : -1, hi = PHASES[i]![1];
  return hold < lo - 0.05 || hold > hi + 0.05 ? raw : was;
}

/**
 * The weight of a ship's people in a boarding: the men at the rail, those
 * fit behind them (and, all hands called, those up from below), by their
 * heart and their practice.
 */
export function boardingWeight(v: Vessel): number {
  const c = v.crew;
  const behind = Math.max(0, c.fighters - c.boarders);
  const men = c.boarders + behind * (v.allHands ? 0.6 : 0.25) + c.reserve * (v.allHands ? 0.45 : 0.12);
  return men * (0.4 + c.morale) * (0.6 + 0.4 * c.gunnery);
}

export class Contacts {
  /** Boarding fights going on, by the attacker's id. */
  readonly fights = new Map<number, BoardingFight>();
  private readonly rng: Rng;
  private readonly sa: Sphere[] = [];
  private readonly sb: Sphere[] = [];
  private fightTimer = 0;

  constructor(seed: number) {
    this.rng = new Rng(seed);
  }

  update(dt: number, vessels: readonly Vessel[], events: SimEvents, wantBoard: (a: Vessel, b: Vessel) => boolean): void {
    for (let i = 0; i < vessels.length; i++) {
      const a = vessels[i]!;
      // (A ship whose state has gone bad must not drag the others down with her.)
      if (!a.alive || !Number.isFinite(a.ship.com.x + a.ship.com.z)) continue;
      for (let j = i + 1; j < vessels.length; j++) {
        const b = vessels[j]!;
        if (!b.alive || !Number.isFinite(b.ship.com.x + b.ship.com.z)) continue;
        const reach = 22 * (a.ship.sx + b.ship.sx) + 10;
        if (Math.abs(a.ship.com.x - b.ship.com.x) > reach || Math.abs(a.ship.com.z - b.ship.com.z) > reach) continue;
        this.collide(dt, a, b, events);
        this.grapple(dt, a, b, events, wantBoard);
      }
    }
    this.fightTimer -= dt;
    if (this.fightTimer <= 0) {
      this.fightTimer = 1;
      for (const a of vessels) {
        if (a.boardingWith < 0 || !a.alive) continue;
        const b = vessels.find((v) => v.id === a.boardingWith);
        if (b && a.id < b.id) this.melee(a, b, events);
      }
    }
    // Fights whose lines have parted (or whose ships are gone) are over.
    for (const [k, f] of this.fights) {
      const a = vessels.find((v) => v.id === f.attacker);
      if (!a || a.boardingWith !== f.defender) this.fights.delete(k);
    }
  }

  /** The boarding fight `v` is in, either side of it. */
  fightOf(v: number): BoardingFight | null {
    const f = this.fights.get(v);
    if (f) return f;
    for (const x of this.fights.values()) if (x.defender === v) return x;
    return null;
  }

  /**
   * Cut the grapnels: the boarders fall back to their own ship (losing a few
   * men as they go), or the boarded ship's people cut the lines - which they
   * can't once the boarders hold a good part of her deck. Returns whether
   * the lines were cut.
   */
  cut(v: Vessel, vessels: readonly Vessel[], events: SimEvents): boolean {
    if (v.boardingWith < 0) return false;
    const f = this.fightOf(v.id);
    if (f && f.defender === v.id && f.hold > 0.35) return false;
    if (f && f.attacker === v.id && f.time > 0) this.casualties(v, 1 + this.rng.int(3), events);
    const o = vessels.find((x) => x.id === v.boardingWith);
    if (f) this.fights.delete(f.attacker);
    castOff(v, vessels);
    v.allHands = false;
    if (o) o.allHands = false;
    return true;
  }

  private collide(dt: number, a: Vessel, b: Vessel, events: SimEvents): void {
    hullSpheres(a, this.sa);
    hullSpheres(b, this.sb);
    for (const p of this.sa) {
      for (const q of this.sb) {
        const dx = p.x - q.x, dz = p.z - q.z;
        const d = Math.hypot(dx, dz);
        const pen = p.r + q.r - d;
        if (pen <= 0 || d < 1e-3) continue;
        const nx = dx / d, nz = dz / d;
        const cx = (p.x + q.x) / 2, cy = (p.y + q.y) / 2, cz = (p.z + q.z) / 2;
        const va = a.ship.pointVelocity(a.ship.worldToLocal(tmp.set(cx, cy, cz), tmp), tmpVa);
        const vb = b.ship.pointVelocity(b.ship.worldToLocal(tmp.set(cx, cy, cz), tmp), tmpVb);
        const vn = (va.x - vb.x) * nx + (va.z - vb.z) * nz;
        const ma = a.ship.mass, mb = b.ship.mass;
        const mu = (ma * mb) / (ma + mb);
        // Stiff springs keep hulls apart; an impulse kills the closing speed.
        const k = mu * 8;
        a.ship.addForce(nx * pen * k, 0, nz * pen * k, cx, cy, cz);
        b.ship.addForce(-nx * pen * k, 0, -nz * pen * k, cx, cy, cz);
        if (vn < -0.3) {
          const j = -1.1 * vn * mu * 0.5;
          a.ship.applyImpulse(nx * j, 0, nz * j, cx, cy, cz);
          b.ship.applyImpulse(-nx * j, 0, -nz * j, cx, cy, cz);
          // Timbers crush: the side struck suffers far more than a stout stem.
          const energy = 0.5 * mu * vn * vn;
          if (energy > 4e4) {
            const la = a.ship.localToDesign(a.ship.worldToLocal(tmp.set(cx, cy, cz), tmp), tmpD);
            const lb = b.ship.localToDesign(b.ship.worldToLocal(tmp.set(cx, cy, cz), tmp), tmpE);
            const aBow = la.x > 12 ? 0.35 : 1;
            const bBow = lb.x > 12 ? 0.35 : 1;
            const dmg = energy / 2.5e4;
            a.damage.damageHull(sectionAt(la.x), Math.sign(la.z) || 1, dmg * aBow / a.cls.hull, events, { x: cx, y: cy, z: cz });
            b.damage.damageHull(sectionAt(lb.x), Math.sign(lb.z) || 1, dmg * bBow / b.cls.hull, events, { x: cx, y: cy, z: cz });
            if (energy > 4e5) {
              if (aBow > 0.5) a.damage.addHole(la.x, 0.2, Math.sign(la.z) * halfBreadthXY(la.x, 0.2), clamp(Math.sqrt(energy) / 2500, 0.2, 1.2));
              if (bBow > 0.5) b.damage.addHole(lb.x, 0.2, Math.sign(lb.z) * halfBreadthXY(lb.x, 0.2), clamp(Math.sqrt(energy) / 2500, 0.2, 1.2));
            }
            events.push({ type: 'collision', x: cx, y: cy + 1, z: cz, energy: clamp(energy / 1e6, 0.05, 1) });
            a.crew.strike(la.x, 3, la.z, 4, 0.12, a.hitCtx(events));
            b.crew.strike(lb.x, 3, lb.z, 4, 0.12, b.hitCtx(events));
          }
        }
      }
    }
    void dt;
  }

  /** Grapnels across when a boarder is alongside and slow enough. */
  private grapple(dt: number, a: Vessel, b: Vessel, events: SimEvents, wantBoard: (a: Vessel, b: Vessel) => boolean): void {
    const d = Math.hypot(a.ship.com.x - b.ship.com.x, a.ship.com.z - b.ship.com.z);
    const beams = 5.5 * (a.ship.sz + b.ship.sz);
    const linked = a.boardingWith === b.id;
    if (!linked) {
      if (a.boardingWith >= 0 || b.boardingWith >= 0) return;
      const aBoards = wantBoard(a, b) && canGrapple(a, b).ok;
      const board = aBoards || (wantBoard(b, a) && canGrapple(b, a).ok);
      if (!board) return;
      a.boardingWith = b.id;
      b.boardingWith = a.id;
      const [att, def] = aBoards ? [a, b] : [b, a];
      this.fights.set(att.id, { attacker: att.id, defender: def.id, hold: START_HOLD, phase: 'hauling', time: 0, weightA: 0, weightD: 0, lostA: 0, lostD: 0, swing: 0 });
      events.push({ type: 'boarding', a: a.id, b: b.id, x: (a.ship.com.x + b.ship.com.x) / 2, z: (a.ship.com.z + b.ship.com.z) / 2 });
    }
    // Grappled: the men heave on the lines, bringing her alongside at a
    // walking pace and holding her there. Lines only pull; the hulls are
    // fended off the last metre or two so they come together without a crash.
    const la = a.ship.worldToLocal(b.ship.com, tmp);
    a.boardingSide = la.z > 0 ? 1 : -1;
    const lb = b.ship.worldToLocal(a.ship.com, tmpD);
    b.boardingSide = lb.z > 0 ? 1 : -1;
    const nx = (b.ship.com.x - a.ship.com.x) / (d || 1), nz = (b.ship.com.z - a.ship.com.z) / (d || 1);
    const want = beams + 1.5;
    const mu = (a.ship.mass * b.ship.mass) / (a.ship.mass + b.ship.mass);
    const dvx = b.ship.vel.x - a.ship.vel.x, dvz = b.ship.vel.z - a.ship.vel.z;
    // Along the line between them: close at up to 0.6 m/s, slowing as she comes.
    const vn = dvx * nx + dvz * nz;
    const closing = -Math.min(0.6, Math.max(0, (d - want) * 0.25));
    const pull = (vn - closing) * mu * 0.9;
    const fn = d > want ? Math.max(0, pull) : pull * 0.5;
    // Across it: the two ships are made to keep pace with each other.
    const tx = dvx - nx * vn, tz = dvz - nz * vn;
    const ft = mu * 0.35;
    a.ship.addForce(nx * fn + tx * ft, 0, nz * fn + tz * ft, a.ship.com.x, a.ship.com.y, a.ship.com.z);
    b.ship.addForce(-nx * fn - tx * ft, 0, -nz * fn - tz * ft, b.ship.com.x, b.ship.com.y, b.ship.com.z);
    // Lines part if the ships are torn apart or one of them is going down.
    if (d - beams > LINE_LENGTH || a.state === 'sinking' || b.state === 'sinking') {
      a.boardingWith = -1;
      b.boardingWith = -1;
    }
    void dt;
  }

  /** A round of hand-to-hand fighting (one a second). */
  private melee(a: Vessel, b: Vessel, events: SimEvents): void {
    if (!a.fighting && !b.fighting) {
      a.boardingWith = b.boardingWith = -1;
      return;
    }
    // Lashed alongside a friend (only the dev tools do that): no fight.
    if (!hostile(a, b) && !hostile(b, a)) return;
    // Lines made fast by the dev tools with no boarder named: the stronger side boards.
    let f = this.fights.get(a.id) ?? this.fights.get(b.id);
    if (!f) {
      const [att, def] = boardingWeight(a) >= boardingWeight(b) ? [a, b] : [b, a];
      f = { attacker: att.id, defender: def.id, hold: START_HOLD, phase: 'hauling', time: 0, weightA: 0, weightD: 0, lostA: 0, lostD: 0, swing: 0 };
      this.fights.set(att.id, f);
    }
    const att = f.attacker === a.id ? a : b, def = att === a ? b : a;
    // Nobody gets across until the lines have brought her alongside.
    if (hullGap(a, b) > BOARDING_GAP) {
      f.phase = 'hauling';
      return;
    }
    // She has struck: the boarders simply take possession.
    for (const [loser, winner] of [[a, b], [b, a]] as const) {
      if (loser.state === 'struck' && winner.fighting) {
        this.carried(f, winner, loser, events);
        return;
      }
    }
    // The captains: boarders go in with all hands; the boarded call all
    // hands once the boarders are fairly aboard; a boarder losing badly,
    // unless his people are reckless, cuts his lines and sheers off.
    for (const v of [att, def]) {
      const cap = v.captain;
      if (!cap || !v.fighting) continue;
      if (v === att) v.allHands = true;
      else if (f.hold > 0.28) v.allHands = true;
    }
    if (att.captain && f.time > 12 && f.hold < 0.08 && f.swing < -0.2 && att.captain.personality.aggression < 0.85) {
      this.cut(att, [a, b], events);
      events.push({ type: 'boardingTurn', attacker: att.id, defender: def.id, phase: 'repelled', hold: 0 });
      return;
    }

    const first = f.phase === 'hauling';
    if (first) f.phase = phaseOf(f.hold);
    f.time += 1;
    const sa = boardingWeight(att), sd = boardingWeight(def);
    f.weightA = sa;
    f.weightD = sd;
    const total = sa + sd + 1e-3;
    const edge = (sa - sd) / total;
    // The deck changes hands by the weight of men, and luck: an even fight
    // sways for a minute or two, a lopsided one is over in under a minute.
    // Whoever holds the most of her deck presses it home, the more so as
    // the fight wears on (nobody stands it for ever).
    const press = 0.03 * (f.hold - 0.45) * Math.min(3, 1 + f.time / 60);
    const move = 0.035 * edge + press + this.rng.gauss() * 0.03;
    f.hold = clamp(f.hold + move, 0, 1);
    f.swing += (Math.sign(move) * Math.min(1, Math.abs(move) / 0.05) - f.swing) * 0.35;
    // A man or so a second falls, mostly on the side losing ground.
    const tilt = clamp(0.5 - move * 8, 0.15, 0.85);
    const lossA = Math.round(this.rng.range(0, 1.1) * tilt * Math.sqrt(Math.max(1, def.crew.boarders + 2) / 8));
    const lossD = Math.round(this.rng.range(0, 1.1) * (1 - tilt) * Math.sqrt(Math.max(1, att.crew.boarders + 2) / 8));
    this.casualties(att, lossA, events);
    this.casualties(def, lossD, events);
    f.lostA += lossA;
    f.lostD += lossD;
    att.crew.morale = clamp(att.crew.morale + (move > 0 ? 0.006 : -0.012), 0, 1);
    def.crew.morale = clamp(def.crew.morale + (move < 0 ? 0.006 : -0.012), 0, 1);
    const phase = phaseOf(f.hold, f.phase);
    // The end of it: her deck carried, or the boarders thrown back (or a side's people broken).
    const attBroken = att.crew.effective < def.crew.effective * 0.2 || att.crew.morale < 0.06 || sa < sd * 0.12;
    const defBroken = def.crew.effective < att.crew.effective * 0.2 || def.crew.morale < 0.06 || sd < sa * 0.12;
    if ((f.hold >= 1 || defBroken) && def.fighting) {
      this.carried(f, att, def, events);
      return;
    }
    if (f.time > 3 && (f.hold <= 0 || attBroken)) {
      att.shock(0.12);
      events.push({ type: 'boardingTurn', attacker: att.id, defender: def.id, phase: 'repelled', hold: 0 });
      this.fights.delete(f.attacker);
      castOff(att, [a, b]);
      att.allHands = def.allHands = false;
      return;
    }
    if (phase !== f.phase || first) {
      f.phase = phase;
      events.push({ type: 'boardingTurn', attacker: att.id, defender: def.id, phase, hold: f.hold });
    }
  }

  /** Her deck is carried: she is taken. */
  private carried(f: BoardingFight, winner: Vessel, loser: Vessel, events: SimEvents): void {
    loser.state = 'captured';
    loser.ship.sails.struck = true;
    loser.ship.sails.setPlan(0);
    loser.battle = false;
    f.hold = 1;
    f.phase = 'carried';
    events.push({ type: 'boardingTurn', attacker: f.attacker, defender: f.defender, phase: 'carried', hold: 1 });
    events.push({ type: 'captured', vessel: loser.id, by: winner.id });
    this.fights.delete(f.attacker);
    winner.boardingWith = loser.boardingWith = -1;
    winner.allHands = loser.allHands = false;
  }

  private casualties(v: Vessel, n: number, events: SimEvents): void {
    for (let k = 0; k < n; k++) {
      // Those at the rail go first; then the men who come up to replace them.
      const front = v.crew.members.filter((m) => m.alive && m.onBoard && m.health > 0.4 && Math.abs(m.z) > deckHalfWidth(m.x) - 1.2);
      const m = front.length ? front[this.rng.int(front.length)]! : null;
      if (m) v.crew.injure(m, this.rng.range(0.6, 1.4), v.hitCtx(events));
      else if (v.crew.reserve > 0) v.crew.reserve--;
    }
  }
}

const tmp = new Vec3(), tmpD = new Vec3(), tmpE = new Vec3(), tmpVa = new Vec3(), tmpVb = new Vec3();
